import { useParams, Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import SEOHead, {
  webPageSchema,
  breadcrumbSchema,
  articleSchema,
  faqSchema,
} from '@/components/feature/SEOHead';
import Breadcrumb, { getArticleBreadcrumb } from '@/components/feature/Breadcrumb';
import { getManhuaBySlug, getCategoryBySlug, ManhuaItem } from '@/mocks/manhua';

export default function Article() {
  const { slug } = useParams<{ slug: string }>();
  const [manhua, setManhua] = useState<ManhuaItem | undefined>(undefined);
  const [activeTab, setActiveTab] = useState<'info' | 'chapters'>('info');

  useEffect(() => {
    if (slug) {
      const found = getManhuaBySlug(slug);
      setManhua(found);
    }
  }, [slug]);

  if (!manhua) {
    return (
      <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-20 text-center">
        <i className="ri-emotion-sad-line text-6xl text-foreground-300"></i>
        <h2 className="mt-4 text-xl font-heading font-semibold text-foreground-700">漫画未找到</h2>
        <p className="mt-2 text-sm text-foreground-500">抱歉，您要查看的漫画不存在或已被移除</p>
        <Link
          to="/category/all"
          className="inline-block mt-6 px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium cursor-pointer hover:bg-primary-600 transition-colors duration-200"
        >
          返回漫画列表
        </Link>
      </div>
    );
  }

  const category = getCategoryBySlug(manhua.category);
  const categoryName = category?.name || '全部';
  const PAGE_TITLE = `${manhua.title} - ${categoryName}韩国漫画 - 韩漫网`;
  const PAGE_DESC = manhua.description.slice(0, 150);
  const PAGE_KEYWORDS = `${manhua.title},${manhua.tags.join(',')},韩国漫画,韩漫网`;

  const schemas = {
    ...webPageSchema({
      title: PAGE_TITLE,
      description: PAGE_DESC,
      url: `/manhua/${manhua.slug}`,
    }),
    ...breadcrumbSchema(
      getArticleBreadcrumb(categoryName, manhua.title).map((item) => ({
        name: item.label,
        url: item.to ? `http://kefaul.cn/${item.to}` : undefined,
      }))
    ),
    ...articleSchema({
      title: manhua.title,
      description: manhua.description,
      image: manhua.cover,
      url: `http://kefaul.cn/manhua/${manhua.slug}`,
      author: manhua.author,
      datePublished: manhua.updatedAt,
      dateModified: manhua.updatedAt,
    }),
    ...faqSchema([
      {
        question: `《${manhua.title}》是什么类型的漫画？`,
        answer: `《${manhua.title}》是一部${categoryName}类型的韩国漫画，由${manhua.author}创作，目前${manhua.status}。评分${manhua.rating}分，累计阅读量超过${(manhua.views / 10000).toFixed(0)}万次。`,
      },
      {
        question: `《${manhua.title}》可以在哪里免费阅读？`,
        answer: `《${manhua.title}》在韩漫网提供免费在线观看，采用下拉式阅读设计，在手机和电脑上都能获得流畅的阅读体验。`,
      },
    ]),
  };

  const formatViews = (views: number) => {
    if (views >= 100000000) return `${(views / 100000000).toFixed(1)}亿`;
    if (views >= 10000) return `${(views / 10000).toFixed(0)}万`;
    return views.toString();
  };

  return (
    <>
      <SEOHead
        title={PAGE_TITLE}
        description={PAGE_DESC}
        keywords={PAGE_KEYWORDS}
        canonicalUrl={`/manhua/${manhua.slug}`}
        ogType="article"
        ogImage={manhua.cover}
        schema={schemas}
      />

      <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-8">
        <Breadcrumb items={getArticleBreadcrumb(categoryName, manhua.title)} />

        <article className="mt-6">
          <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
            <div className="w-full lg:w-[40%]">
              <div className="flex items-center gap-3 mb-5">
                <span className="px-3 py-1 bg-accent-100 text-accent-700 text-xs font-medium rounded-full">
                  {manhua.status}
                </span>
                <span className="text-xs text-foreground-500">{manhua.author} · 著</span>
              </div>

              <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-950 leading-tight">
                {manhua.title}
              </h1>
              <p className="mt-3 text-lg font-heading font-light text-foreground-400">
                {manhua.tags.join(' · ')}
              </p>

              <div className="mt-6 flex items-center gap-8">
                <div className="text-right">
                  <div className="text-3xl font-heading font-bold text-primary-600">{manhua.rating}</div>
                  <div className="text-xs text-foreground-500">评分</div>
                </div>
                <div className="text-right">
                  <div className="text-3xl font-heading font-bold text-accent-600">{formatViews(manhua.views)}</div>
                  <div className="text-xs text-foreground-500">阅读量</div>
                </div>
              </div>

              <p className="mt-6 text-sm text-foreground-600 leading-relaxed">
                {manhua.description}
              </p>

              <div className="mt-6 flex flex-wrap gap-2">
                {manhua.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1 bg-secondary-100 text-secondary-700 text-xs rounded-full"
                  >
                    {tag}
                  </span>
                ))}
              </div>

              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <button
                  onClick={() => setActiveTab('chapters')}
                  className="px-6 py-2.5 bg-primary-500 text-background-50 text-sm font-medium rounded-full whitespace-nowrap cursor-pointer hover:bg-primary-600 transition-colors duration-200"
                >
                  开始阅读 <i className="ri-play-line ml-1"></i>
                </button>
                <button className="px-6 py-2.5 border border-background-300 text-foreground-700 text-sm font-medium rounded-full whitespace-nowrap cursor-pointer hover:bg-background-100 transition-colors duration-200">
                  <i className="ri-bookmark-line mr-1"></i> 加入收藏
                </button>
              </div>
            </div>

            <div className="w-full lg:w-[60%]">
              <div className="relative">
                <div className="grid grid-cols-2 gap-4">
                  <div className="rounded-xl overflow-hidden">
                    <img
                      src={manhua.cover}
                      alt={`${manhua.title}封面`}
                      title={`${manhua.title} - 韩漫网韩国漫画`}
                      className="w-full h-auto rounded-xl"
                    />
                  </div>
                  <div className="rounded-xl overflow-hidden mt-8">
                    <img
                      src="https://readdy.ai/api/search-image?query=Korean%20manhwa%20interior%20page%20preview%20showing%20beautiful%20comic%20panel%20layout%20with%20characters%20and%20dialogue%20bubbles%2C%20clean%20black%20and%20white%20line%20art%20with%20grayscale%20tones%2C%20dynamic%20composition%2C%20professional%20webtoon%20style&width=600&height=900&seq=manhua-preview&orientation=portrait"
                      alt={`${manhua.title}预览`}
                      title={`${manhua.title} - 内容预览`}
                      className="w-full h-auto rounded-xl"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-12">
            <div className="flex items-center border-b border-background-200 mb-6">
              <button
                onClick={() => setActiveTab('info')}
                className={`px-5 py-3 text-sm font-medium whitespace-nowrap cursor-pointer transition-colors duration-200 border-b-2 -mb-px ${
                  activeTab === 'info'
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-foreground-500 hover:text-foreground-700'
                }`}
              >
                作品详情
              </button>
              <button
                onClick={() => setActiveTab('chapters')}
                className={`px-5 py-3 text-sm font-medium whitespace-nowrap cursor-pointer transition-colors duration-200 border-b-2 -mb-px ${
                  activeTab === 'chapters'
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-foreground-500 hover:text-foreground-700'
                }`}
              >
                章节列表 ({manhua.chapters.length})
              </button>
            </div>

            {activeTab === 'info' && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-background-100 rounded-xl p-5">
                  <h3 className="text-base font-heading font-semibold text-foreground-900 mb-3">作品信息</h3>
                  <dl className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <dt className="text-foreground-500">作者</dt>
                      <dd className="text-foreground-700 font-medium">{manhua.author}</dd>
                    </div>
                    <div className="flex justify-between">
                      <dt className="text-foreground-500">分类</dt>
                      <dd className="text-foreground-700 font-medium">{categoryName}</dd>
                    </div>
                    <div className="flex justify-between">
                      <dt className="text-foreground-500">状态</dt>
                      <dd className="text-foreground-700 font-medium">{manhua.status}</dd>
                    </div>
                    <div className="flex justify-between">
                      <dt className="text-foreground-500">评分</dt>
                      <dd className="text-foreground-700 font-medium">{manhua.rating} / 10</dd>
                    </div>
                    <div className="flex justify-between">
                      <dt className="text-foreground-500">阅读量</dt>
                      <dd className="text-foreground-700 font-medium">{formatViews(manhua.views)}</dd>
                    </div>
                    <div className="flex justify-between">
                      <dt className="text-foreground-500">更新时间</dt>
                      <dd className="text-foreground-700 font-medium">{manhua.updatedAt}</dd>
                    </div>
                  </dl>
                </div>

                <div className="bg-background-100 rounded-xl p-5">
                  <h3 className="text-base font-heading font-semibold text-foreground-900 mb-3">读者评价</h3>
                  <div className="space-y-4">
                    {[
                      { user: '小甜饼爱好者', comment: '超级好看！画风精美，剧情紧凑，每次更新都第一时间来看！', rating: 5 },
                      { user: '漫画达人', comment: '这部漫画的剧情真的很棒，人物刻画细腻，强烈推荐！', rating: 5 },
                      { user: '深夜读者', comment: '熬夜看完的，根本停不下来！期待下一话！', rating: 4 },
                    ].map((review, idx) => (
                      <div key={idx} className="border-b border-background-200/70 pb-3 last:border-0 last:pb-0">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-foreground-800">{review.user}</span>
                          <div className="flex items-center gap-0.5">
                            {[1, 2, 3, 4, 5].map((s) => (
                              <i
                                key={s}
                                className={`text-[10px] ${s <= review.rating ? 'ri-star-fill text-amber-500' : 'ri-star-line text-foreground-300'}`}
                              ></i>
                            ))}
                          </div>
                        </div>
                        <p className="mt-1 text-xs text-foreground-500 leading-relaxed">{review.comment}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'chapters' && (
              <div className="space-y-2">
                {manhua.chapters.map((chapter) => (
                  <div
                    key={chapter.id}
                    className="flex items-center justify-between bg-background-100 rounded-lg px-4 py-3 cursor-pointer hover:bg-background-200 transition-colors duration-200"
                  >
                    <div className="flex items-center gap-3">
                      <span className="w-7 h-7 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 text-xs font-bold">
                        {chapter.number}
                      </span>
                      <span className="text-sm font-medium text-foreground-800">{chapter.title}</span>
                    </div>
                    <span className="text-xs text-foreground-400">{chapter.date}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </article>
      </div>
    </>
  );
}